import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {checkDataModsReasonRequired} from 'audit/AuditDAO';
import {view as contextMenuView} from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import EditableComponent from 'notebook/mediocre/core-ui/EditableComponent';
import {PasteEvent, SelectionPartition} from 'notebook/mediocre/EditorTypes';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {
  MediocreSearchResultProps,
  forwardSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {NoteEditor} from 'notebook/notes/NoteEditorTypes';
import {ACTION_RENAME_TEXT_BOX, ACTION_SUBMIT_TEXT_BOX} from 'notebook/store/VersioningConstants';
import {performServerSideWriteActionWithAudits} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {isNoteFillInTextBox} from 'notebook/TypePredicates';
import {FileAnalyticsParams, getFileAnalyticsMetadata} from 'notebook/util/analytics/FileAnalyticsUtils';
import {isNotePartWithItem, NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as keyboard from 'util/Keyboard';
import * as net from 'util/Net';
import {BasicEvent, ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

import {NoteFillInTextBox} from './constants/FillInTextBoxConstants';
import {hasUnsubmittedValues} from './FillInTextBoxUtils';
import RenameTextBoxModal from './RenameTextBoxModal';

export interface CancelEditModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export interface NoteFillInTextBoxEditableProps extends MediocreSearchResultProps {
  editor: NoteEditor;
  cid: string;
  fileId: string;
  index: number;
  isConfig: boolean;
  locked: boolean;
  isProtected?: boolean;
  textBox: NoteFillInTextBox;
  viewer: boolean;
  spellCheck: boolean;
  itemUpdateHandler: (
    index: number,
    e: {
      text: string;
      formats: Format | null;
      name: string;
      lastFinalizedValue: NoteFillInTextBox['lastFinalizedValue'];
      target?: Element | EventTarget;
      selection?: BenchlingSelection;
    }
  ) => void;
  formatHandler: (
    indexInfo: {index: number},
    e: {
      format: string;
      partition: SelectionPartition;
    }
  ) => void;
  splitHandler: (
    index: number,
    e: SelectionPartition & {
      modifierKey: boolean;
      shiftKey: boolean;
      originalEvent: ElementKeyboardEvent;
    }
  ) => void;
  deleteHandler: (index: number, e?: {alwaysDelete: boolean}) => void;
  moveHandler: (
    index: number,
    e: {
      up: boolean;
      beginning: boolean;
    }
  ) => void;
  cursorHandler: (index: number, e: {position: 'start' | 'end'}) => void;
  tabHandler: (
    index: number,
    e: {
      partition: SelectionPartition;
      shiftKey: boolean;
      target: EventTarget;
    }
  ) => void;
  pasteHandler: (index: number, e: PasteEvent) => void;
  setSelectionAbove: (index: number) => void;
  setSelectionBelow: (index: number) => void;
  insertItemHandler: (index: number, type?: string | null) => void;
  setClipboard: (index: number, e: BasicEvent) => void;
  isLockedWithEditWarn: () => boolean;
  storeState: (args: {selection: BenchlingSelection}) => void;
  restoreState: (e: ElementKeyboardEvent, selectionRestoreData: {selection: SelectionRestoreData}) => boolean;
  requestSelection: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
}

function NoteFillInTextBoxEditable(props: NoteFillInTextBoxEditableProps): JSX.Element {
  const {text, formats, name, lastFinalizedValue} = props.textBox;
  const hasEverBeenSynced = props.textBox.lastFinalizedValue != null;
  const isUnsubmitted = hasUnsubmittedValues(props.textBox);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDataModsEnabled, setIsDataModsEnabled] = useState(false);
  useEffect(() => {
    const checkDataMods = async (): Promise<void> => {
      const dataModsEnabled = (await checkDataModsReasonRequired([props.fileId]))?.data_mods_reason_required;
      setIsDataModsEnabled(dataModsEnabled);
    };
    checkDataMods();
  }, [props.fileId]);
  const styles = StyleSheet.create({
    button: {
      paddingLeft: '8px',
      paddingRight: '8px',
      boxShadow: 'none',
      color: '#2b9be',
      ':last-child': {
        marginRight: '5px',
      },
      ':hover': {
        color: '#5293FF',
      },
    },
    buttonGroup: {
      display: 'flex',
      alignItems: 'flex-start',
    },
    textBoxEditable: {
      marginLeft: 10,
      flexGrow: 1,
      overflow: 'hidden',
    },
  });

  function isNameUnique(name: string): boolean {
    return !props.editor
      .getRoot()
      .noteParts.some(
        (notepart: NotePart) =>
          isNotePartWithItem(notepart) && isNoteFillInTextBox(notepart.item) && notepart.item.name === name
      );
  }

  function _copyTextBoxBlockHandler(e: BasicEvent): void {
    props.setClipboard(props.index, e);
    e.preventDefault();
  }

  function _itemUpdateHandler(
    index: number,
    e: {
      text: string;
      formats: Format | null;
      name: string;
      lastFinalizedValue: NoteFillInTextBox['lastFinalizedValue'];
      target?: Element | EventTarget;
      selection?: BenchlingSelection;
    }
  ): void {
    if (!props.locked) {
      props.itemUpdateHandler(index, e);
    } else {
      props.isLockedWithEditWarn();
    }
  }

  function formatHandler(e: {format: string; partition: SelectionPartition}): void {
    props.formatHandler({index: props.index}, e);
  }

  function _textUpdateHandler(e: {
    text: string;
    target: Element | EventTarget;
    formats: Format;
    selection?: BenchlingSelection;
  }): void {
    _itemUpdateHandler(props.index, {
      ...props.textBox,
      ...e,
    });
    props.requestSelection({selection: e.selection || selutil.getSelectionIndices()});
  }

  function _deleteHandler(
    index: number,
    e: {alwaysDelete: boolean},
    fileAnalyticsParams: FileAnalyticsParams
  ): void {
    if (props.isConfig) {
      analytics.trackEvent(ELN_ANALYTICS_ACTIONS.DELETE_TEXT_BOX, {
        ...getFileAnalyticsMetadata(fileAnalyticsParams),
      });
      props.deleteHandler(index, e);
    }
  }

  function _cutHandler(e: BasicEvent, fileAnalyticsParams: FileAnalyticsParams): void {
    e.preventDefault();
    if (!props.locked) {
      _copyTextBoxBlockHandler(e);
      _deleteHandler(props.index, {alwaysDelete: true}, fileAnalyticsParams);
    } else {
      props.isLockedWithEditWarn();
    }
  }

  function _pasteHandler(e: PasteEvent): void {
    if (!props.locked) {
      props.pasteHandler(props.index, e);
    } else {
      props.isLockedWithEditWarn();
    }
  }

  function _hiddenKeyDownHandler(e: ElementKeyboardEvent, fileAnalyticsParams: FileAnalyticsParams): void {
    // Cut and copy will be handled by onCut / onCopy handlers
    if (keyboard.isCtrlX(e) || keyboard.isCtrlC(e)) {
      return;
    }

    // There are no other keyboard interactions that should use the browser default to manipulate the text in
    // the hidden contentEditable.
    e.preventDefault();

    if (keyboard.isUndoRedo(e)) {
      if (!props.locked) {
        props.restoreState(e, {selection: selutil.getSelectionIndices()});
      } else {
        props.isLockedWithEditWarn();
      }
      return;
    }

    // Prevent [backspace, delete, enter] below from modifying if the text box block is locked
    if ([8, 46, 13].includes(e.which) && props.isLockedWithEditWarn()) {
      return;
    }

    switch (e.which) {
      case 8: // BACKSPACE
        _deleteHandler(props.index, {alwaysDelete: true}, fileAnalyticsParams);
        props.setSelectionAbove(props.index);
        break;
      case 46: // DELETE
        _deleteHandler(props.index, {alwaysDelete: true}, fileAnalyticsParams);
        props.setSelectionBelow(props.index);
        break;
      case 13: {
        // ENTER
        const newIndex = e.shiftKey ? props.index - 1 : props.index;
        props.insertItemHandler(newIndex);
        break;
      }
      case 38: // UP
        props.moveHandler(props.index, {up: true, beginning: true});
        break;
      case 40: // DOWN
        props.moveHandler(props.index, {up: false, beginning: true});
        break;
      case 37: // LEFT
        props.cursorHandler(props.index, {position: 'start'});
        break;
      case 39: // RIGHT
        props.cursorHandler(props.index, {position: 'end'});
        break;
      default:
        break;
    }
  }

  function _submitHandler(): void {
    performServerSideWriteActionWithAudits({
      entryId: props.fileId,
      changeSummary: '1 text box will be modified',
      loadingScreenMessage: 'Submitting Text Box...',
      autoSaverAction: ACTION_SUBMIT_TEXT_BOX,
      doPerformAction: async ({dataModsHeader}) =>
        await net.postJSONPromise({
          url: `/1/api/entries/${props.fileId}/text-boxes/${props.textBox.provenanceUuid}/action/submit-textbox`,
          extraHeaders: dataModsHeader,
        }),
      areFeatureDataModsEnabled: hasEverBeenSynced && isDataModsEnabled,
    });
  }

  async function _renameHandler(newName: string, currentName: string): Promise<void> {
    let dataModsCanceled = false;
    await performServerSideWriteActionWithAudits({
      entryId: props.fileId,
      changeSummary: 'Text box name will be modified',
      loadingScreenMessage: 'Submitting Text Box Name...',
      autoSaverAction: ACTION_RENAME_TEXT_BOX,
      doPerformAction: async ({dataModsHeader}) =>
        await net.postJSONPromise({
          url: `/1/api/entries/${props.fileId}/text-boxes/${props.textBox.provenanceUuid}/action/submit-name`,
          data: {
            oldValue: currentName,
            newValue: newName,
          },
          extraHeaders: dataModsHeader,
        }),
      areFeatureDataModsEnabled: isDataModsEnabled,
      onRequestClose: () => {
        dataModsCanceled = true;
      },
    });
    if (!dataModsCanceled) {
      _itemUpdateHandler(props.index, {
        ...props.textBox,
        name: newName,
      });
    }
  }

  function _renderTitle(): JSX.Element | null {
    return name !== '' ? (
      <div className="mediocre-textBoxEditable-title" onContextMenu={openTextBoxContextMenu}>
        {name}
      </div>
    ) : null;
  }

  function openTextBoxContextMenu(e: ElementMouseEvent): void {
    e.preventDefault();
    let message = props.isProtected && !props.isConfig ? 'Cannot edit title of fill-in-only texbox' : null;
    message = props.locked ? 'Cannot edit locked entry' : message;
    if (message) {
      notifications.warn(message);
      return;
    }
    contextMenuView.open(
      e.nativeEvent,
      [
        {
          name: 'Rename Textbox',
          click: () => {
            setIsModalOpen(true);
          },
        },
      ],
      true
    );
  }

  return (
    <EditorPropertiesContext.Consumer>
      {({rootFolderId: projectId, fileId, templateCollectionId}) => {
        const fileAnalyticsParams = {projectId, fileId, templateCollectionId};
        return (
          <div {...getTestProps({component: 'FillInTextBoxEditable', element: 'root'})}>
            <div
              className="hiddenFocusEditable editable"
              data-cid={NoteFillInTextBoxEditable.hiddenFocusCid(props.cid)}
              {...getTestProps({component: 'FillInTextBoxEditable', element: 'hiddenFocus'})}
              contentEditable={true}
              onKeyDown={(e) => {
                _hiddenKeyDownHandler(e, fileAnalyticsParams);
              }}
              onCopy={_copyTextBoxBlockHandler}
              onCut={(e) => {
                _cutHandler(e, fileAnalyticsParams);
              }}
            >
              {null}
            </div>
            {_renderTitle()}
            <ModalAnchor open={isModalOpen}>
              <RenameTextBoxModal
                fileId={props.fileId}
                initialName={name}
                isNameUnique={isNameUnique}
                setNameAndClose={(name: string) => {
                  const currentName = props.textBox.name;
                  if (name != null) {
                    if (props.isConfig) {
                      _itemUpdateHandler(props.index, {
                        ...props.textBox,
                        name,
                      });
                    } else {
                      _renameHandler(name, currentName);
                    }
                  }
                  setIsModalOpen(false);
                }}
              />
            </ModalAnchor>
            <div
              className="mediocre-textBoxEditable"
              data-mediocre-item-buffered-select={1}
              style={{display: 'flex'}}
              onContextMenu={openTextBoxContextMenu}
            >
              <EditableComponent
                className={classNames(css(styles.textBoxEditable), 'notebook-export--text-box-editable')}
                index={props.index}
                cid={props.cid}
                text={text || ''}
                allowRichText={true}
                allowAtSearch={true}
                allowSlashSearch={false}
                allowMultiline={true}
                allowDefaultPaste={true}
                formats={formats}
                formatHandler={formatHandler}
                storeState={props.storeState}
                restoreState={props.restoreState}
                updateHandler={_textUpdateHandler}
                pasteHandler={_pasteHandler}
                locked={props.locked}
                spellCheck={props.spellCheck}
                // Bind is used just to set the first parameter being called. React will rebind all of these to
                // anyhow, null is used to stop react from throwing warnings
                splitHandler={props.splitHandler.bind(null, props.index)}
                mergeUpHandler={() => {
                  _deleteHandler(props.index, {alwaysDelete: true}, fileAnalyticsParams);
                }}
                moveHandler={props.moveHandler.bind(null, props.index)}
                tabHandler={props.tabHandler.bind(null, props.index)}
                {...forwardSearchResultProps(props)}
              />
              {/* props.viewer hides buttons in print preview */}
              {!props.viewer && isDataModsEnabled && !props.isConfig && (
                <div
                  className={classNames(css(styles.buttonGroup), 'notebook-export--text-box-button-group')}
                >
                  {hasEverBeenSynced && (
                    <Tooltip title="Reset unsubmitted changes">
                      <Button
                        type="button"
                        id="refresh-button"
                        className={classNames(
                          css(styles.button),
                          'u-mini-spacing-left',
                          'notebook-export--text-box-button'
                        )}
                        buttonStyle={ButtonStyle.TERTIARY_GRAY}
                        size={ButtonSize.MINI}
                        iconAltText="Refresh textbox (resets unsubmitted changes)"
                        icon="arrows-rotate"
                        disabled={!isUnsubmitted}
                        onClick={() => {
                          _itemUpdateHandler(props.index, {
                            ...props.textBox,
                            text: lastFinalizedValue?.text || '',
                            formats: lastFinalizedValue?.formats || formatters.defaultFormats(),
                          });
                        }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip title={props.locked ? 'Entry is locked' : ''}>
                    <Button
                      type="button"
                      id="submit-button"
                      className={classNames(
                        css(styles.button),
                        'u-mini-spacing-left',
                        'notebook-export--text-box-button'
                      )}
                      buttonStyle={ButtonStyle.SECONDARY}
                      size={ButtonSize.MINI}
                      disabled={!isUnsubmitted || props.locked}
                      text="Submit"
                      onClick={_submitHandler}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        );
      }}
    </EditorPropertiesContext.Consumer>
  );
}

NoteFillInTextBoxEditable.hiddenFocusCid = (cid: string): string => `${cid}-textFocus`;
export default NoteFillInTextBoxEditable;
